import React, { useEffect, useMemo, useState } from "react";
import "./agent.css";
import {
  GET_AI_AGENTS,
  POST_AI_AGENT,
  DELETE_AI_AGENT,
} from "../../api/aiKnowledge.service";

const Agent = () => {
  const [agentName, setAgentName] = useState("");
  const providerModels = {
    openai: ["gpt-4o-mini", "gpt-4o"],
    anthropic: ["claude-3.5"],
    google: ["gemini-1.5-pro", "gemini-1.5-flash"],
  };

  const [provider, setProvider] = useState("openai");
  const [model, setModel] = useState(providerModels.openai[0]);
  const [search, setSearch] = useState("");
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const mapApiAgent = (agent) => ({
    id: agent?.id,
    name: agent?.agentName || "",
    provider: agent?.providerName || "",
    model: agent?.llmModel || agent?.llmModal || "",
    status: Number(agent?.isActive) === 1 ? "Active" : "Paused",
    updatedAt: agent?.updatedAt ? new Date(agent.updatedAt).toLocaleString() : "",
    raw: agent,
  });

  const fetchAgents = async () => {
    setLoading(true);
    setError("");
    try {
      const resp = await GET_AI_AGENTS();
      const rows = Array.isArray(resp?.data)
        ? resp.data
        : Array.isArray(resp?.data?.data)
          ? resp.data.data
          : resp?.data?.rows || [];
      setAgents(rows.map(mapApiAgent));
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load agents.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgents();
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!agentName.trim()) return;
    setSaving(true);
    setError("");

    try {
      const payload = {
        agentName: agentName.trim(),
        providerName: provider,
        llmModel: model,
      };
      await POST_AI_AGENT(payload);

      await fetchAgents();
      setAgentName("");
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to save agent.");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const options = providerModels[provider] || [];
    if (options.length === 0) return;
    if (!options.includes(model)) {
      setModel(options[0]);
    }
  }, [provider]);

  const handleDelete = async (agentId) => {
    setSaving(true);
    setError("");
    try {
      await DELETE_AI_AGENT(agentId);
      await fetchAgents();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to delete agent.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setAgentName("");
    setProvider("openai");
    setModel(providerModels.openai[0]);
  };

  const filteredAgents = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return agents;
    return agents.filter((agent) =>
      `${agent.name} ${agent.provider} ${agent.model}`
        .toLowerCase()
        .includes(term),
    );
  }, [agents, search]);

  return (
      <div className="agent-ui__shell">
        <div className="agent-ui__topbar"></div>

        <div className="agent-ui__grid">
          <form className="agent-ui__card" onSubmit={handleSubmit}>
            <div className="agent-ui__card-header">
              <div className="agent-ui__card-title">
                Create Agent
              </div>
              <div className="agent-ui__card-subtitle">
                Configure provider, model, and deployment settings.
              </div>
            </div>

            <div className="agent-ui__body">
              <label className="agent-ui__label">
                Agent Name <span className="agent-ui__required">*</span>
              </label>
              <input
                className="agent-ui__input"
                value={agentName}
                onChange={(event) => setAgentName(event.target.value)}
                placeholder="e.g. Support Assistant"
              />
              {error ? <div className="agent-ui__hint">{error}</div> : null}

              <label className="agent-ui__label">Provider</label>
              <select
                className="agent-ui__input"
                value={provider}
                onChange={(event) => setProvider(event.target.value)}
              >
                <option value="openai">OpenAI</option>
                <option value="anthropic">Anthropic</option>
                <option value="google">Google</option>
              </select>
              <div className="agent-ui__hint">
                Select the AI service provider for this agent.
              </div>

              <label className="agent-ui__label">Model</label>
              <select
                className="agent-ui__input"
                value={model}
                onChange={(event) => setModel(event.target.value)}
              >
                {(providerModels[provider] || []).map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
              <div className="agent-ui__hint">
                Select the LLM model that the agent will use.
              </div>
            </div>

            <div className="agent-ui__footer">
              <button
                type="button"
                className="agent-ui__btn agent-ui__btn--ghost"
                onClick={handleCancel}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="agent-ui__btn agent-ui__btn--primary"
                disabled={saving}
              >
                {saving ? "Saving..." : "Create Agent"}
              </button>
            </div>
          </form>

          <div className="agent-ui__list">
            <div className="agent-ui__list-header">
              <div>
                <div className="agent-ui__card-title">Agent Directory</div>
                <div className="agent-ui__card-subtitle">
                  Central list of configured agents and status.
                </div>
              </div>
              <input
                className="agent-ui__search"
                placeholder="Search agents"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>

            <div className="agent-ui__table">
              <div className="agent-ui__table-row agent-ui__table-head">
                <div>Agent</div>
                <div>Provider</div>
                <div>Model</div>
                <div>Status</div>
                <div>Updated</div>
                <div>Actions</div>
              </div>
              {loading ? (
                <div className="agent-ui__empty">Loading agents...</div>
              ) : filteredAgents.length === 0 ? (
                <div className="agent-ui__empty">
                  No agents found for this search.
                </div>
              ) : (
                filteredAgents.map((agent) => (
                  <div key={agent.id} className="agent-ui__table-row">
                    <div>
                      <div className="agent-ui__agent-name">{agent.name}</div>
                      <div className="agent-ui__agent-id">
                        #{String(agent.id).padStart(3, "0")}
                      </div>
                    </div>
                    <div className="agent-ui__cell">{agent.provider}</div>
                    <div className="agent-ui__cell">{agent.model}</div>
                    <div>
                      <span
                        className={`agent-ui__status ${
                          agent.status === "Active"
                            ? "agent-ui__status--active"
                            : "agent-ui__status--paused"
                        }`}
                      >
                        {agent.status}
                      </span>
                    </div>
                    <div className="agent-ui__cell">{agent.updatedAt}</div>
                    <div className="agent-ui__actions">
                      <button
                        type="button"
                        className="agent-ui__link agent-ui__link--danger"
                        onClick={() => handleDelete(agent.id)}
                        disabled={saving}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
  );
};

export default Agent;
